import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { palettes, productColors, type Mode, type Palette, type Product } from '@infinix/tokens';
import { buildTheme } from './theme';
import { CssVars } from './globals';

interface ModeCtx {
  mode: Mode;
  setMode: (m: Mode) => void;
  toggle: () => void;
  p: Palette;
  product: (name: Product) => string;
}

const Ctx = createContext<ModeCtx | null>(null);
const KEY = 'ix.mode';

function readStored(): Mode | null {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

/** The console is light only (decision A-5); setMode/toggle remain for components that read the mode. */
export function ModeProvider({ children }: { children: ReactNode }) {
  // Decision A-5 (2026-10-06): the console is light only. A stored 'dark' preference is ignored and cleared.
  const [mode, setModeState] = useState<Mode>('light');
  useEffect(() => { if (readStored()) { try { window.localStorage.removeItem(KEY); } catch { /* ignore */ } } }, []);

  const setMode = useCallback((m: Mode) => {
    setModeState(m);
    try {
      window.localStorage.setItem(KEY, m);
    } catch {
      /* storage unavailable: keep the in-memory choice */
    }
  }, []);

  useEffect(() => {
    document.documentElement.dataset.ixMode = mode;
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo<ModeCtx>(
    () => ({
      mode,
      setMode,
      toggle: () => setMode(mode === 'light' ? 'dark' : 'light'),
      p: palettes[mode],
      product: (name) => productColors[mode][name],
    }),
    [mode, setMode],
  );

  return (
    <Ctx.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CssVars />
        {children}
      </ThemeProvider>
    </Ctx.Provider>
  );
}

export function useMode(): ModeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMode must be used inside <ModeProvider>');
  return v;
}
